import { useMutation, useQuery } from '@tanstack/react-query';
import { RefreshCw, RotateCcw } from 'lucide-react';
import { Alert, Button } from '@thegpvc/ui';
import { api } from '../api/client';
import { QueueMetrics } from './QueueMetrics';
import { TaskList } from './TaskList';
import { useState, useEffect } from 'react';
import { text } from '../styles/theme';
import { errorMessage } from '../utils/errorMessage';

// TaskQueuePanel is the task-queue admin surface, embedded in Settings ▸ Admin
// (it was the standalone /tasks page before the 2026-07 IA cleanup).
export function TaskQueuePanel() {
  const [refreshInterval] = useState(1000); // 1 second
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isPageVisible, setIsPageVisible] = useState(true);

  // Track page visibility to pause refreshing when tab is hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const shouldRefetch = autoRefresh && isPageVisible;

  const { data: queueStatus, isLoading, error, refetch } = useQuery({
    queryKey: ['taskQueueStatus'],
    queryFn: () => api.getTaskQueueStatus(),
    refetchInterval: shouldRefetch ? refreshInterval : false,
  });

  const { data: tasksData, refetch: refetchTasks } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => api.listTasks({ limit: 50 }),
    refetchInterval: shouldRefetch ? refreshInterval : false,
  });

  const requeueMutation = useMutation({
    mutationFn: () => api.requeueFailedTasks(),
    onSuccess: () => {
      refetch();
      refetchTasks();
    },
  });

  const handleRequeueFailed = () => {
    const failedCount = queueStatus?.statusCounts.failed ?? 0;
    const prompt = failedCount > 0
      ? `Requeue all ${failedCount} failed task(s) back to pending? This resets attempts and lets the worker retry them.`
      : 'Requeue all failed tasks back to pending? This resets attempts and lets the worker retry them.';
    if (window.confirm(prompt)) {
      requeueMutation.mutate();
    }
  };

  if (isLoading) {
    return <div className={`px-4 py-8 text-center ${text.secondary}`}>Loading…</div>;
  }

  if (error) {
    return <Alert variant="error">Failed to load task queue: {errorMessage(error)}</Alert>;
  }

  if (!queueStatus) {
    return null;
  }

  const handleRefresh = () => {
    refetch();
    refetchTasks();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <label className={`flex items-center gap-2 text-sm ${text.secondary}`}>
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            className="rounded border-navy-300 dark:border-navy-600 text-orange-500 focus:ring-orange-500"
          />
          Auto-refresh (1s)
          {!isPageVisible && <span className={`text-xs ${text.muted}`}>(paused)</span>}
        </label>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleRequeueFailed}
          disabled={requeueMutation.isPending || (queueStatus.statusCounts.failed ?? 0) === 0}
        >
          <RotateCcw className={`w-4 h-4 ${requeueMutation.isPending ? 'animate-spin' : ''}`} />
          Requeue Failed
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleRefresh}
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Requeue result banner */}
      {requeueMutation.isSuccess && requeueMutation.data && (
        <Alert variant="success">{requeueMutation.data.message}</Alert>
      )}
      {requeueMutation.isError && (
        <Alert variant="error">
          Failed to requeue tasks: {errorMessage(requeueMutation.error)}
        </Alert>
      )}

      {/* Queue Metrics */}
      <QueueMetrics status={queueStatus} />

      {/* Task List */}
      <TaskList tasks={tasksData?.tasks || []} total={tasksData?.total || '0'} />
    </div>
  );
}
